import {Injectable} from "@angular/core";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {BehaviorSubject, Observable, throwError} from "rxjs";
import {catchError, tap, finalize} from "rxjs/operators";
import {environment} from "environments/environment";
import {Inventario} from "app/modules/inventario/modal/inventario";
import {Bioma} from "app/model/bioma";
import {Municipio} from "app/model/municipio";
import {TipoFormulario} from "app/model/TipoFormulario";
import { DadosPessoa } from "app/model/dadosPessoa";

@Injectable({
  providedIn: "root",
})
export class InventarioService {
  private _inventarios: BehaviorSubject<Inventario[]> = new BehaviorSubject<
    Inventario[]
  >([]);

  private periods: Record<
    string,
    {inicioMes: number; inicioAno: number; fimMes: number; fimAno: number}
  > = {};

  private _statusFormulario: BehaviorSubject<TipoFormulario | null> =
    new BehaviorSubject<TipoFormulario | null>(null);

  private _dadosPessoa: BehaviorSubject<DadosPessoa> = new BehaviorSubject<DadosPessoa>(null);

  // ponytail: cache de sessão. Exclusão em outra aba passa até o F5.
  private _inventariosConfirmados = new Set<string>();

  get statusFormulario$(): Observable<TipoFormulario | null> {
    return this._statusFormulario.asObservable();
  }

  /**
   * Construtor
   */
  constructor(private _httpClient: HttpClient) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Acessores
  // -----------------------------------------------------------------------------------------------------

  /**
   * Observable para acessar os inventários
   */
  get inventarios$(): Observable<Inventario[]> {
    return this._inventarios.asObservable();
  }

  get dadosPessoa$(): Observable<any>{
    return this._dadosPessoa.asObservable();
  }

  ufInventario$ = new BehaviorSubject<string | null>(null);
  loading$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<string | null>(null);


  // -----------------------------------------------------------------------------------------------------
  // @ Métodos públicos
  // -----------------------------------------------------------------------------------------------------

  inventarioJaConfirmado(id: string): boolean {
    return this._inventariosConfirmados.has(id);
  }

  /**
   * Lista todos os inventários
   */
  listarInventario(): Observable<Inventario[]> {
    return this._httpClient
      .get<Inventario[]>(`${environment.api}/inventario`)
      .pipe(
        tap(inventarios => {
          this._inventarios.next(inventarios);
        }),
        catchError(error =>
          this.handleError(error, "Erro ao listar inventários")
        )
      );
  }

  /**
   * Salva um novo inventário
   */
  salvarInventario(inventario: Inventario): Observable<Inventario> {
    return this._httpClient
      .post<Inventario>(`${environment.api}/inventario/create`, inventario)
      .pipe(
        tap(salvo => this.confirmarInventario(salvo?.id)),
        catchError(error =>
          this.handleError(error, "Erro ao salvar inventário")
        )
      );
  }

  /**
   * Busca bioma por coordenadas
   */
  getBiomaByCoordinates(
    latitude: number,
    longitude: number
  ): Observable<Bioma> {
    const url = `${environment.api}/inventario/bioma`;
    return this._httpClient
      .post<Bioma>(url, {latitude, longitude})
      .pipe(
        catchError(error =>
          this.handleError(error, "Erro ao buscar bioma por coordenadas")
        )
      );
  }

  /**
   * Busca município por coordenadas
   */
  getMunicipioByCoordinates(
    latitude: number,
    longitude: number
  ): Observable<Municipio> {
    const url = `${environment.api}/inventario/municipio`;
    return this._httpClient
      .post<Municipio>(url, {latitude, longitude})
      .pipe(
        catchError(error =>
          this.handleError(error, "Erro ao buscar município por coordenadas")
        )
      );
  }

  buscarUfPorInventario(id: string): Observable<string>  {
    this.loading$.next(true);

    const url = `${environment.api}/inventario/uf/${id}/sigla`;
    return this._httpClient
      .get(url, { responseType: 'text' })
      .pipe(
        tap((uf: string) => {
          this.ufInventario$.next(uf);
          this.error$.next(null);
        }),
        catchError((err) => {
          this.error$.next('Erro ao buscar UF do inventário');
          return throwError(() => err);
        }),
        finalize(() => this.loading$.next(false))
      ) as Observable<string>;
  }

  /**
   * Busca inventário por ID
   */
  buscarInventarioPorId(inventarioId: string): Observable<Inventario> {
    return this._httpClient
      .get<Inventario>(
        `${environment.api}/inventario/buscarInventario/${inventarioId}`
      )
      .pipe(
        tap(() => this.confirmarInventario(inventarioId)),
        catchError(error =>
          this.handleError(error, "Erro ao buscar inventário por ID")
        )
      );
  }

  /**
   * Mesma busca, sem o handleError que troca HttpErrorResponse por Error.
   * O guard precisa do status (404 x 5xx).
   */
  buscarInventarioExistente(inventarioId: string): Observable<Inventario> {
    return this._httpClient
      .get<Inventario>(
        `${environment.api}/inventario/buscarInventario/${inventarioId}`
      )
      .pipe(tap(() => this.confirmarInventario(inventarioId)));
  }

  /**
   * Atualiza um inventário existente
   */
  update(id: string, inventario: Inventario): Observable<Inventario> {
    return this._httpClient
      .put<Inventario>(`${environment.api}/inventario/${id}`, inventario)
      .pipe(
        tap(() => this.confirmarInventario(id)),
        catchError(error =>
          this.handleError(error, "Erro ao atualizar inventário")
        )
      );
  }

  /**
   * Deleta um inventário por ID
   */
  delete(id: string): Observable<void> {
    return this._httpClient
      .delete<void>(`${environment.api}/inventario/${id}`)
      .pipe(
        catchError(error =>
          this.handleError(error, "Erro ao deletar inventário")
        )
      );
  }

  /**
   * Armazena localmente o período de um inventário
   */
  setPeriod(
    idInventario: string,
    period: {
      inicioMes: number;
      inicioAno: number;
      fimMes: number;
      fimAno: number;
    }
  ) {
    console.log("Setando período:", {idInventario, period});
    this.periods[idInventario] = period;
  }

  /**
   * Recupera o período armazenado de um inventário
   */
  getPeriod(
    idInventario: string
  ):
    | {inicioMes: number; inicioAno: number; fimMes: number; fimAno: number}
    | undefined {
    return this.periods[idInventario];
  }

  /**
   * Recupera o status do formulário de um inventário
   */
  getStatusFormulario(idInventario: string): Observable<TipoFormulario> {
    return this._httpClient
      .get<TipoFormulario>(
        `${environment.api}/inventario/${idInventario}/status-formularios`
      )
      .pipe(
        tap(status => {
          this._statusFormulario.next(status);
        }),
        catchError(error =>
          this.handleError(error, "Erro ao buscar status do formulário")
        )
      );
  }

  obterDadosPessoaPorCPF(cpfRaw: string): Observable<DadosPessoa>{
    const cpf = cpfRaw.trim();
    return this._httpClient.get<DadosPessoa>(`${environment.api}/inventario/buscar-nome-pessoa-por-cpf/${cpf}`).pipe(
      tap((dadosPessoa: DadosPessoa) => {
        this._dadosPessoa.next(dadosPessoa);
      }),
      catchError((error) => {
        throw error;
      })
    )
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Métodos privados
  // -----------------------------------------------------------------------------------------------------

  private confirmarInventario(id: string | undefined): void {
    if (id) {
      this._inventariosConfirmados.add(id);
    }
  }

  /**
   * Tratamento de erros
   */
  private handleError(
    error: HttpErrorResponse,
    message: string
  ): Observable<never> {
    let errorMessage = message;

    if (error.error instanceof ErrorEvent) {
      errorMessage += ` | Erro: ${error.error.message}`;
    } else {
      errorMessage += ` | Código do erro: ${error.status} | Mensagem: ${error.message}`;
    }

    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }
}